/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import { faChevronDown, faChevronUp, faEllipsisVertical, faPlus } from "@fortawesome/pro-light-svg-icons";
import { faPaperPlane, faTrashXmark, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { RefreshButton, useAppBar, useDialogs } from "../../../components";
import { Child, Communication, CommunicationSendRecipient, SMSLog } from "../../../models";
import {
  useDeleteCommunicationMutation,
  useGetAllChildrenFilteredQuery,
  useGetCommunicationSendRecipientsQuery,
  useGetCommunicationsFilteredQuery,
  useGetCommunicationSmsPrefixQuery,
  useRetryCommunicationLogMutation,
  useSendCommunicationMutation,
} from "../../../redux/apiStore";
import { getChildImageUrlFromChild } from "../../../utils";
import { formatAsShortDateTime, parseDate } from "../../../utils/date";
import { formatPhoneNumber } from "../../../utils/string";

const COMMUNICATION_ROW_HEIGHT = 60;
const COMMUNICATION_OVERSCAN_ROWS = 5;

function getCommunicationStatus(logs: SMSLog[]) {
  const successCount = logs.filter((x) => x.isSuccess).length;
  const errorCount = logs.filter((x) => !x.isSuccess).length;

  if (successCount > 0 && errorCount > 0) {
    return { label: "Partially Sent", color: "warning" as const };
  }

  if (successCount === 0 && errorCount > 0) {
    return { label: "Error", color: "error" as const };
  }

  return { label: "Queued", color: "info" as const };
}

function getLogStatus(log: SMSLog) {
  if (!log.isSuccess) {
    return { label: log.statusMessage || "Error", color: "error" as const };
  }

  const message = (log.statusMessage || "Queued").toLowerCase();
  if (message.includes("deliver")) {
    return { label: log.statusMessage, color: "success" as const };
  }

  return { label: log.statusMessage || "Queued", color: "info" as const };
}

function SmsPhonePreview(props: { message: string; prefix: string; minHeight?: number }) {
  const { message, prefix, minHeight = 740 } = props;

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: "linear-gradient(180deg, #eaf2ff 0%, #f6f7fb 100%)",
        p: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 300,
          maxWidth: "100%",
          aspectRatio: "9 / 19.5",
          borderRadius: 6,
          border: "10px solid #111",
          backgroundColor: "#0f1117",
          p: 1,
          boxShadow: "0 20px 55px rgba(0,0,0,0.3)",
        }}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: 4,
            backgroundColor: "#f3f3f6",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: 34, backgroundColor: "#0f1117", borderBottomLeftRadius: 14, borderBottomRightRadius: 14, mx: "auto", width: 120 }} />
          <Box sx={{ p: 1.5, borderBottom: "1px solid #ddd", backgroundColor: "#fff" }}>
            <Typography variant="body2" fontWeight={600} textAlign="center">
              Messages
            </Typography>
          </Box>

          <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", flexGrow: 1, alignItems: "flex-start" }}>
            <Box
              sx={{
                maxWidth: "82%",
                backgroundColor: "#30d158",
                color: "#111",
                borderRadius: 3,
                px: 1.5,
                py: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.92rem",
                lineHeight: 1.35,
              }}
            >
              {(prefix || "") + (message || "")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function NewCommunicationDrawer(props: { open: boolean; onClose: () => void; children: Child[]; onSent: () => void }) {
  const { open, onClose, children, onSent } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const [selectedChildId, setSelectedChildId] = useState<number>(0);
  const [message, setMessage] = useState<string>("");
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string>("");
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const [sendCommunication, sendStatus] = useSendCommunicationMutation();
  const recipientsQuery = useGetCommunicationSendRecipientsQuery({ childId: selectedChildId > 0 ? selectedChildId : undefined }, { skip: !open });
  const smsPrefixQuery = useGetCommunicationSmsPrefixQuery({});

  useEffect(() => {
    if (!open) {
      setSelectedChildId(0);
      setMessage("");
      setSelectedPhones(new Set());
      setError("");
      return;
    }

    if (drawerContentRef.current) {
      drawerContentRef.current.scrollTop = 0;
    }
  }, [open]);

  const recipients = useMemo(() => recipientsQuery.data ?? [], [recipientsQuery.data]);

  useEffect(() => {
    const validPhones = new Set(recipients.map((x) => x.phoneNumber));
    setSelectedPhones((current) => {
      const updated = new Set(Array.from(current).filter((phone) => validPhones.has(phone)));
      // Only update if phones actually changed
      if (updated.size === current.size && Array.from(updated).every((phone) => current.has(phone))) {
        return current;
      }
      return updated;
    });
  }, [recipients]);

  const handleChildChange = (event: SelectChangeEvent<number>) => {
    setSelectedChildId(Number(event.target.value));
    setSelectedPhones(new Set());
    setError("");
  };

  const toggleRecipient = (phoneNumber: string) => {
    setSelectedPhones((current) => {
      const next = new Set(current);
      if (next.has(phoneNumber)) {
        next.delete(phoneNumber);
      } else {
        next.add(phoneNumber);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPhones(new Set(recipients.map((x) => x.phoneNumber)));
  };

  const deselectAll = () => {
    setSelectedPhones(new Set());
  };

  const allRecipientsSelected = recipients.length > 0 && selectedPhones.size === recipients.length;
  const someRecipientsSelected = selectedPhones.size > 0 && !allRecipientsSelected;

  const handleSend = async () => {
    setError("");

    if (!message.trim()) {
      setError("Please enter a message.");
      return;
    }

    if (message.length > 300) {
      setError("Message can be up to 300 characters.");
      return;
    }

    if (selectedPhones.size < 1) {
      setError("Please select at least one recipient.");
      return;
    }

    try {
      const result = await sendCommunication({
        childId: selectedChildId > 0 ? selectedChildId : undefined,
        message,
        recipientPhones: Array.from(selectedPhones),
      }).unwrap();
      enqueueSnackbar(
        `Communication sent successfully to ${result.totalSuccess} recipient(s)${result.totalErrors > 0 ? ` (${result.totalErrors} failed)` : ""}${result.totalUnsubscribed > 0 ? ` (${result.totalUnsubscribed} unsubscribed)` : ""}`,
        { variant: "success" },
      );
      onClose();
      onSent();
    } catch (err: any) {
      const errorMsg = err?.data ?? "Failed to send communication.";
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", md: "620px" },
            zIndex: 1550,
          },
        },
      }}
    >
      <Stack ref={drawerContentRef} sx={{ p: 2, gap: 2, height: "100%", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Send SMS Communication</Typography>
          <IconButton size="small" sx={{ width: 44, height: 44 }} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <FormControl fullWidth variant="filled">
          <InputLabel id="communication-child-label">Child</InputLabel>
          <Select<number> labelId="communication-child-label" label="Child" value={selectedChildId} onChange={handleChildChange}>
            <MenuItem value={0}>All Children</MenuItem>
            {_.orderBy(children, [(x) => `${x.firstName} ${x.lastName}`.toLowerCase()], ["asc"]).map((child) => (
              <MenuItem key={child.id} value={child.id}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Avatar alt={`${child.firstName} ${child.lastName}`} src={getChildImageUrlFromChild(child)} sx={{ width: 24, height: 24 }} />
                  <Typography>
                    {child.firstName} {child.lastName}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="filled"
          label="Message"
          multiline
          minRows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value.slice(0, 300))}
          helperText={`${300 - message.length} characters left`}
        />

        <Typography variant="overline">Recipients</Typography>

        <TableContainer component={Paper} sx={{ flexShrink: 0, maxHeight: 340, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 52 }}>
                  <Checkbox
                    sx={{ py: 0 }}
                    checked={allRecipientsSelected}
                    indeterminate={someRecipientsSelected}
                    onChange={() => {
                      if (allRecipientsSelected) {
                        deselectAll();
                      } else {
                        selectAll();
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipientsQuery.isFetching && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading recipients...</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!recipientsQuery.isFetching &&
                recipients.map((recipient: CommunicationSendRecipient) => {
                  const isChecked = selectedPhones.has(recipient.phoneNumber);
                  return (
                    <TableRow
                      key={`${recipient.id}-${recipient.phoneNumber}`}
                      hover
                      onClick={() => toggleRecipient(recipient.phoneNumber)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Checkbox
                          sx={{ py: 0 }}
                          checked={isChecked}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleRecipient(recipient.phoneNumber);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography>{recipient.name}</Typography>
                      </TableCell>
                      <TableCell>{formatPhoneNumber(recipient.phoneNumber)}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="overline">Preview</Typography>
        <SmsPhonePreview message={message} prefix={smsPrefixQuery.data?.prefix ?? ""} />

        {selectedPhones.size < 1 && <FormHelperText error>Select at least one recipient to send.</FormHelperText>}

        <Stack direction="column" gap={2}>
          <Button
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
            disabled={sendStatus.isLoading || selectedPhones.size < 1}
            onClick={handleSend}
            sx={{ height: 50, width: "100%" }}
          >
            Send
          </Button>
          <Button
            variant="outlined"
            disabled={sendStatus.isLoading}
            onClick={onClose}
            sx={{ height: 50, width: "100%" }}
            startIcon={<FontAwesomeIcon icon={faXmark} />}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

function CommunicationDetailsDrawer(props: {
  open: boolean;
  communication: Communication | null;
  child: Child | null;
  childName: string;
  prefix: string;
  onClose: () => void;
  onUpdated: () => void;
  onDelete?: (communicationId: number) => Promise<void>;
}) {
  const { open, communication, child, childName, prefix, onClose, onUpdated, onDelete } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [retryCommunicationLog, retryStatus] = useRetryCommunicationLogMutation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string>("");

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, logId: number) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedLogId(logId);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setSelectedLogId(null);
  };

  const selectedLog = communication?.log.find((x) => x.id === selectedLogId) ?? null;

  const handleRetry = async () => {
    if (!communication || !selectedLog) {
      closeMenu();
      return;
    }

    setActionError("");
    try {
      await retryCommunicationLog({ communicationId: communication.id, logId: selectedLog.id }).unwrap();
      closeMenu();
      onUpdated();
    } catch (err: any) {
      setActionError(err?.data ?? "Failed to retry sending SMS.");
      closeMenu();
    }
  };

  const status = communication ? getCommunicationStatus(communication.log) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1550 }}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", md: "760px" },
            zIndex: 1550,
          },
        },
      }}
    >
      <Stack sx={{ p: 2, gap: 2, height: "100%", overflowY: "auto" }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h6">Communication Details</Typography>
          <Box flexGrow={1} />
          <IconButton size="small" sx={{ width: 44, height: 44 }} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Stack>

        {actionError && <Alert severity="error">{actionError}</Alert>}

        {!communication && <Typography>Select a communication to view details.</Typography>}

        {communication && (
          <>
            <Stack direction={{ xs: "column", md: "row" }} gap={2} width="100%" justifyContent="space-between">
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline">Child</Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  {child && <Avatar alt={childName} src={getChildImageUrlFromChild(child)} sx={{ width: 24, height: 24 }} />}
                  <Typography>{childName}</Typography>
                </Stack>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline">Status</Typography>
                <Box>{status && <Chip label={status.label} color={status.color} variant="outlined" size="small" />}</Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline">Date</Typography>
                <Typography>{formatAsShortDateTime(parseDate(communication.createdCentralDateTime))}</Typography>
              </Box>
            </Stack>

            <Typography variant="overline">Recipients</Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 360, overflowY: "auto", flexShrink: 0 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 52, display: { xs: "none", md: "table-cell" } }}></TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Sent</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {communication.log.map((log) => {
                    const rowStatus = getLogStatus(log);
                    return (
                      <TableRow key={log.id} hover onClick={(event) => handleOpenMenu(event, log.id)}>
                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                          <IconButton sx={{ width: 40, height: 40 }} onClick={(event) => handleOpenMenu(event, log.id)}>
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </IconButton>
                        </TableCell>
                        <TableCell>{log.recipientName}</TableCell>
                        <TableCell>{formatPhoneNumber(log.recipientPhone)}</TableCell>
                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>{formatAsShortDateTime(parseDate(log.sentCentralDateTime))}</TableCell>
                        <TableCell>
                          <Chip label={rowStatus.label} color={rowStatus.color} variant="outlined" size="small" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box>
              <Typography variant="overline">Preview</Typography>
              <SmsPhonePreview message={communication.message ?? ""} prefix={prefix} minHeight={520} />
            </Box>

            {/* Delete and close buttons at bottom */}
            <Stack direction="column" spacing={2} sx={{ flexShrink: 0, width: "100%" }}>
              <Button variant="outlined" onClick={onClose} startIcon={<FontAwesomeIcon icon={faXmark} />} sx={{ height: 50, width: "100%" }}>
                Close
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={async () => {
                  if (onDelete && communication) {
                    try {
                      await onDelete(communication.id);
                      onClose();
                    } catch (err) {
                      // Error handling is done in the parent component
                    }
                  }
                }}
                startIcon={<FontAwesomeIcon icon={faTrashXmark} />}
                sx={{ height: 50, width: "100%" }}
              >
                Delete
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={closeMenu}>
        <MenuItem disabled={!selectedLog || selectedLog.isSuccess || retryStatus.isLoading} onClick={handleRetry}>
          Retry Send
        </MenuItem>
      </Menu>
    </Drawer>
  );
}

export function CommunicationsPage() {
  const appBar = useAppBar();
  const dialogs = useDialogs();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedChildId, setSelectedChildId] = useState<number>(0);
  const communicationsQuery = useGetCommunicationsFilteredQuery({ childId: selectedChildId > 0 ? selectedChildId : undefined });
  const childrenQuery = useGetAllChildrenFilteredQuery({ showInactive: true });
  const smsPrefixQuery = useGetCommunicationSmsPrefixQuery({});
  const [deleteCommunication, deleteCommunicationStatus] = useDeleteCommunicationMutation();
  const [selectedCommunicationId, setSelectedCommunicationId] = useState<number | null>(null);
  const [isCreateDrawerOpen, setCreateDrawerOpen] = useState<boolean>(false);
  const [mainMenuAnchorEl, setMainMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuCommunicationId, setMenuCommunicationId] = useState<number | null>(null);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [expandedCommunicationIds, setExpandedCommunicationIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    appBar.setDefaults();
    appBar.setTitle("Communications");
    appBar.setRightContent(
      <RefreshButton
        onRefresh={() => {
          Promise.all([communicationsQuery.refetch(), childrenQuery.refetch(), smsPrefixQuery.refetch()]);
        }}
      />,
    );
  }, []);

  const childById = useMemo(() => {
    const map = new Map<number, Child>();
    (childrenQuery.data ?? []).forEach((child) => map.set(child.id, child));
    return map;
  }, [childrenQuery.data]);

  const orderedCommunications = useMemo(() => {
    return _.orderBy(communicationsQuery.data ?? [], [(x) => parseDate(x.createdCentralDateTime)?.getTime() ?? 0], ["desc"]);
  }, [communicationsQuery.data]);

  const viewportHeight = 600; // Default height for virtualization calculation; actual table height is determined by flexbox
  const totalRows = orderedCommunications.length;
  const visibleRows = Math.ceil(viewportHeight / COMMUNICATION_ROW_HEIGHT) + COMMUNICATION_OVERSCAN_ROWS * 2;
  const startIndex = Math.max(0, Math.floor(scrollTop / COMMUNICATION_ROW_HEIGHT) - COMMUNICATION_OVERSCAN_ROWS);
  const endIndex = Math.min(totalRows, startIndex + visibleRows);
  const topSpacerHeight = startIndex * COMMUNICATION_ROW_HEIGHT;
  const bottomSpacerHeight = Math.max(0, (totalRows - endIndex) * COMMUNICATION_ROW_HEIGHT);
  const virtualizedCommunications = orderedCommunications.slice(startIndex, endIndex);
  const displayedCommunications = isSmallScreen ? orderedCommunications : virtualizedCommunications;

  const selectedCommunication = orderedCommunications.find((x) => x.id === selectedCommunicationId) ?? null;
  const selectedChild = selectedCommunication ? (childById.get(selectedCommunication.childId) ?? null) : null;
  const selectedChildName = selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName}` : "Unknown Child";

  const activeChildren = (childrenQuery.data ?? []).filter((x) => x.isActive && !x.deleted);

  const closeMainMenu = () => {
    setMainMenuAnchorEl(null);
    setMenuCommunicationId(null);
  };

  const handleOpenMainMenu = (event: React.MouseEvent<HTMLElement>, communicationId: number) => {
    event.stopPropagation();
    setMainMenuAnchorEl(event.currentTarget);
    setMenuCommunicationId(communicationId);
  };

  const toggleCommunicationExpanded = (communicationId: number) => {
    setExpandedCommunicationIds((current) => {
      const next = new Set(current);
      if (next.has(communicationId)) {
        next.delete(communicationId);
      } else {
        next.add(communicationId);
      }
      return next;
    });
  };

  const handleDeleteCommunication = async () => {
    if (!menuCommunicationId) {
      closeMainMenu();
      return;
    }

    const confirmed = await dialogs.confirm("Are you sure you want to delete this communication?");
    if (!confirmed) {
      closeMainMenu();
      return;
    }

    try {
      await deleteCommunication({ id: menuCommunicationId }).unwrap();
      if (selectedCommunicationId === menuCommunicationId) {
        setSelectedCommunicationId(null);
      }
      closeMainMenu();
      communicationsQuery.refetch();
    } catch (err) {
      closeMainMenu();
    }
  };

  const loadingRow = (
    <TableRow>
      <TableCell>
        <Skeleton width={140} height={28} />
      </TableCell>
      <TableCell>
        <Skeleton width={160} height={28} />
      </TableCell>
      <TableCell>
        <Skeleton width={300} height={28} />
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "calc(100vh - 64px)", boxSizing: "border-box" }}>
      <Stack gap={1} sx={{ flexGrow: 1, minHeight: 0, flexDirection: "column" }}>
        <Stack direction={{ xs: "column", md: "row" }} gap={1} sx={{ width: "100%" }}>
          <FormControl
            sx={{ flex: { xs: 1, md: "0 0 360px" }, width: { xs: "100%", md: "auto" }, "& .MuiOutlinedInput-root": { height: { xs: 50, md: "auto" } } }}
          >
            <InputLabel id="communications-child-filter-label">Child Filter</InputLabel>
            <Select<number>
              labelId="communications-child-filter-label"
              value={selectedChildId}
              label="Child Filter"
              onChange={(event: SelectChangeEvent<number>) => {
                setSelectedChildId(Number(event.target.value));
                setScrollTop(0);
              }}
            >
              <MenuItem value={0}>All Children</MenuItem>
              {_.orderBy(activeChildren, [(x) => `${x.firstName} ${x.lastName}`.toLowerCase()], ["asc"]).map((child) => (
                <MenuItem key={child.id} value={child.id}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Avatar alt={`${child.firstName} ${child.lastName}`} src={getChildImageUrlFromChild(child)} sx={{ width: 24, height: 24 }} />
                    <Typography>
                      {child.firstName} {child.lastName}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box flexGrow={1} sx={{ display: { xs: "none", md: "block" } }} />
          <Button startIcon={<FontAwesomeIcon icon={faPlus} />} variant="outlined" onClick={() => setCreateDrawerOpen(true)} sx={{ height: 50 }}>
            New SMS
          </Button>
        </Stack>

        <TableContainer
          component={Paper}
          onScroll={(event) => setScrollTop((event.currentTarget as HTMLDivElement).scrollTop)}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 165, whiteSpace: "nowrap" }}>Date</TableCell>
                <TableCell sx={{ minWidth: 180, whiteSpace: "nowrap" }}>Child</TableCell>
                {!isSmallScreen && <TableCell>Message</TableCell>}
                {isSmallScreen && <TableCell sx={{ width: 52 }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {communicationsQuery.isFetching && [loadingRow, loadingRow, loadingRow]}
              {!communicationsQuery.isFetching && !isSmallScreen && topSpacerHeight > 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ p: 0, border: 0, height: `${topSpacerHeight}px` }} />
                </TableRow>
              )}
              {!communicationsQuery.isFetching &&
                displayedCommunications.map((communication) => {
                  const child = childById.get(communication.childId);
                  const childName = child ? `${child.firstName} ${child.lastName}` : "Unknown Child";
                  const isExpanded = expandedCommunicationIds.has(communication.id);
                  return (
                    <Fragment key={communication.id}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer", height: isSmallScreen ? "auto" : `${COMMUNICATION_ROW_HEIGHT}px` }}
                        onClick={() => setSelectedCommunicationId(communication.id)}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatAsShortDateTime(parseDate(communication.createdCentralDateTime))}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                            {child && <Avatar alt={childName} src={getChildImageUrlFromChild(child)} sx={{ width: 24, height: 24 }} />}
                            <Typography noWrap>{childName}</Typography>
                          </Stack>
                        </TableCell>
                        {!isSmallScreen && (
                          <TableCell>
                            <Typography
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                              }}
                            >
                              {communication.message}
                            </Typography>
                          </TableCell>
                        )}
                        {isSmallScreen && (
                          <TableCell align="center" sx={{ p: 0.5 }}>
                            <IconButton
                              size="small"
                              sx={{ width: 32, height: 32 }}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleCommunicationExpanded(communication.id);
                              }}
                            >
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} size="sm" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                      {isSmallScreen && isExpanded && (
                        <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                          <TableCell colSpan={3}>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {communication.message}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              {!communicationsQuery.isFetching && !isSmallScreen && bottomSpacerHeight > 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ p: 0, border: 0, height: `${bottomSpacerHeight}px` }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Menu anchorEl={mainMenuAnchorEl} open={!!mainMenuAnchorEl} onClose={closeMainMenu}>
        <MenuItem disabled={deleteCommunicationStatus.isLoading} onClick={handleDeleteCommunication}>
          Delete Communication
        </MenuItem>
      </Menu>

      <CommunicationDetailsDrawer
        open={!!selectedCommunication}
        communication={selectedCommunication}
        child={selectedChild}
        childName={selectedChildName}
        prefix={smsPrefixQuery.data?.prefix ?? ""}
        onClose={() => setSelectedCommunicationId(null)}
        onUpdated={() => communicationsQuery.refetch()}
        onDelete={async (communicationId: number) => {
          const confirmed = await dialogs.confirm("Are you sure you want to delete this communication?");
          if (!confirmed) {
            return;
          }
          try {
            await deleteCommunication({ id: communicationId }).unwrap();
            setSelectedCommunicationId(null);
            await communicationsQuery.refetch();
          } catch (err) {
            // Error will be displayed by the main page
          }
        }}
      />

      <NewCommunicationDrawer
        open={isCreateDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        children={activeChildren}
        onSent={() => communicationsQuery.refetch()}
      />
    </Box>
  );
}
